import { memo, useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { DocintDashboardResponse } from "@/api/admin-api"

import { getDocintDashboard } from "@/api/admin-api"
import { AlertTriangle, FileText, RefreshCw, Users } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"

function formatSEK(value: number): string {
  return `${value.toFixed(2)} SEK`
}

function getProgressVariant(percentage: number): "default" | "warning" | "destructive" {
  if (percentage >= 100) return "destructive"
  if (percentage >= 80) return "warning"
  return "default"
}

function getProgressClassName(variant: "default" | "warning" | "destructive"): string {
  if (variant === "destructive") return "[&>div]:bg-destructive-ink"
  if (variant === "warning") return "[&>div]:bg-warning-ink"
  return ""
}

function getUserRowClassName(isBlocked: boolean, isWarning: boolean): string {
  if (isBlocked) {
    return "bg-destructive/15 border border-destructive text-foreground"
  }
  if (isWarning) {
    return "bg-warning/15 border border-warning text-foreground"
  }
  return "bg-muted/50"
}

type QuotaProgressProps = {
  label: string
  spend: number
  limit: number
  period: "daily" | "monthly"
}

function QuotaProgress({ label, spend, limit, period }: QuotaProgressProps) {
  const percentage = limit > 0 ? Math.min((spend / limit) * 100, 100) : 0
  const variant = getProgressVariant(percentage)

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {label} ({period})
        </span>
        <span className="font-medium">
          {formatSEK(spend)} / {formatSEK(limit)}
        </span>
      </div>
      <Progress className={getProgressClassName(variant)} value={percentage} />
      <div className="text-muted-foreground text-right text-xs">{percentage.toFixed(1)}%</div>
    </div>
  )
}

type PoolStatusCardProps = {
  name: string
  spend: number
  pageCount: number
  callCount: number
  monthlyLimit: number
  isBlocked: boolean
}

function PoolStatusCard({
  name,
  spend,
  pageCount,
  callCount,
  monthlyLimit,
  isBlocked,
}: PoolStatusCardProps) {
  const { t } = useTranslation()
  const monthlyPercentage = monthlyLimit > 0 ? (spend / monthlyLimit) * 100 : 0

  return (
    <div
      className={`rounded-lg border p-3 ${isBlocked ? "bg-destructive/15 border-destructive text-foreground border" : ""}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">{name}</span>
        {isBlocked && (
          <Badge className="text-xs" variant="destructive">
            {t("adminDashboard.docint.blocked", "Blocked")}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.docint.spend", "Spend")}
          </div>
          <div className="font-medium">{formatSEK(spend)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.docint.pages", "Pages")}
          </div>
          <div className="font-medium">{pageCount.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.docint.calls", "Calls")}
          </div>
          <div className="font-medium">{callCount.toLocaleString()}</div>
        </div>
      </div>
      <div className="mt-2">
        <Progress
          className={getProgressClassName(getProgressVariant(monthlyPercentage))}
          value={Math.min(monthlyPercentage, 100)}
        />
      </div>
    </div>
  )
}

export const DocintQuotaCard = memo(function DocintQuotaCard() {
  const { t } = useTranslation()

  const { data, isLoading, refetch, isFetching } = useQuery<DocintDashboardResponse>({
    queryKey: ["admin", "docint", "dashboard"],
    queryFn: () => getDocintDashboard(),
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  const hasBlockedEntities = useMemo(() => {
    return data?.blocked_entities && data.blocked_entities.length > 0
  }, [data])

  const hasWarningEntities = useMemo(() => {
    return data?.warning_entities && data.warning_entities.length > 0
  }, [data])

  if (isLoading) {
    return (
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <FileText className="size-5" />
            <CardTitle className="text-lg">
              {t("adminDashboard.docint.title", "Document Intelligence Quota")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <div className="space-y-2">
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-2 w-full" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return (
      <Card className="rounded-lg shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <FileText className="size-5" />
            <CardTitle className="text-lg">
              {t("adminDashboard.docint.title", "Document Intelligence Quota")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground py-4 text-center text-sm">
            {t(
              "adminDashboard.docint.notConfigured",
              "Document Intelligence tracking not configured"
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="size-5" />
            <CardTitle className="text-lg">
              {t("adminDashboard.docint.title", "Document Intelligence Quota")}
            </CardTitle>
          </div>
          <Button size="icon" variant="ghost" onClick={() => refetch()}>
            <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <CardDescription>
          {t("adminDashboard.docint.period", "Period: {{yearMonth}}", {
            yearMonth: data.year_month,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Blocked entities warning */}
        {hasBlockedEntities && (
          <div className="bg-destructive/15 border-destructive text-foreground rounded-lg border p-3">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="text-destructive size-4 shrink-0" />
              <span className="text-destructive font-medium">
                {t("adminDashboard.docint.quotaExceeded", "Quota Exceeded")}
              </span>
            </div>
            <div className="space-y-1 text-sm">
              {data.blocked_entities.map((entity, idx) => (
                <div
                  key={`${entity.scope}-${entity.period}-${idx}`}
                  className="bg-background/50 flex items-center justify-between rounded px-2 py-1"
                >
                  <div className="flex items-center gap-2">
                    <Badge className="text-xs" variant="destructive">
                      {entity.type === "user" ? entity.user_id?.slice(0, 8) + "..." : entity.scope}
                    </Badge>
                    <span className="text-destructive text-xs">{entity.period}</span>
                  </div>
                  <span className="text-destructive text-xs">
                    {formatSEK(entity.current_spend_sek)} / {formatSEK(entity.limit_sek)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning entities section */}
        {hasWarningEntities && !hasBlockedEntities && (
          <div className="bg-warning/15 border-warning text-foreground rounded-lg border p-3">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="text-warning size-4 shrink-0" />
              <span className="text-warning font-medium">
                {t("adminDashboard.docint.approachingLimit", "Approaching Quota Limit")}
              </span>
            </div>
            <div className="space-y-1 text-sm">
              {data.warning_entities.slice(0, 5).map((entity, idx) => (
                <div
                  key={`${entity.scope}-${entity.period}-${idx}`}
                  className="bg-background/50 flex items-center justify-between rounded px-2 py-1"
                >
                  <div className="flex items-center gap-2">
                    <Badge className="border-warning text-warning text-xs" variant="outline">
                      {entity.type === "user" ? entity.user_id?.slice(0, 8) + "..." : entity.scope}
                    </Badge>
                    <span className="text-warning text-xs">
                      {entity.period} - {entity.percentage}%
                    </span>
                  </div>
                  <span className="text-warning text-xs">
                    {formatSEK(entity.current_spend_sek)} / {formatSEK(entity.limit_sek)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Global quota progress */}
        <div className="space-y-3">
          <div className="text-sm font-medium">
            {t("adminDashboard.docint.globalQuota", "Global Quota")}
          </div>
          <QuotaProgress
            label={t("adminDashboard.docint.monthly", "Monthly")}
            limit={data.global.monthly.limit_sek}
            period="monthly"
            spend={data.global.monthly.spend_sek}
          />
          <QuotaProgress
            label={t("adminDashboard.docint.daily", "Daily")}
            limit={data.global.daily.limit_sek}
            period="daily"
            spend={data.global.daily.spend_sek}
          />
        </div>

        {/* Pool status cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <PoolStatusCard
            callCount={data.pools.user.call_count}
            isBlocked={data.pools.user.is_blocked}
            monthlyLimit={data.pools.user.monthly_limit_sek}
            name={t("adminDashboard.docint.userPool", "User Pool")}
            pageCount={data.pools.user.page_count}
            spend={data.pools.user.spend_sek}
          />
          <PoolStatusCard
            callCount={data.pools.indexing.call_count}
            isBlocked={data.pools.indexing.is_blocked}
            monthlyLimit={data.pools.indexing.monthly_limit_sek}
            name={t("adminDashboard.docint.indexingPool", "Indexing Pool")}
            pageCount={data.pools.indexing.page_count}
            spend={data.pools.indexing.spend_sek}
          />
        </div>

        {/* Top users summary with status badges */}
        {data.top_users.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Users className="size-4" />
              {t("adminDashboard.docint.topUsers", "Top Users")}
            </div>
            <div className="space-y-1">
              {data.top_users.slice(0, 5).map((user) => {
                const isBlocked = user.status === "blocked"
                const isWarning = user.status === "warning"
                const percentage = Math.max(user.daily_percentage, user.monthly_percentage)

                return (
                  <div
                    key={user.user_id}
                    className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm ${getUserRowClassName(isBlocked, isWarning)}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate text-xs" title={user.user_id}>
                        {user.profile?.name ||
                          user.profile?.email ||
                          (user.user_id.length > 16
                            ? `${user.user_id.slice(0, 8)}...${user.user_id.slice(-4)}`
                            : user.user_id)}
                      </span>
                      {isBlocked && (
                        <Badge className="px-1.5 py-0 text-[10px]" variant="destructive">
                          {t("adminDashboard.docint.blocked", "Blocked")}
                          {user.status_period && ` (${user.status_period})`}
                        </Badge>
                      )}
                      {isWarning && (
                        <Badge
                          className="border-warning text-warning px-1.5 py-0 text-[10px]"
                          variant="outline"
                        >
                          {percentage.toFixed(0)}%{user.status_period && ` ${user.status_period}`}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">{formatSEK(user.spend_sek)}</span>
                      <span className="text-muted-foreground/60">
                        / {formatSEK(user.monthly_limit_sek)}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Active overrides indicator */}
        {data.config.active_overrides > 0 && (
          <div className="text-muted-foreground text-center text-xs">
            {t("adminDashboard.docint.activeOverrides", "{{count}} active override(s)", {
              count: data.config.active_overrides,
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
})
